import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "./Reveal";
import { GALLERY } from "@/data/content";

export default function Gallery() {
    const [openIndex, setOpenIndex] = useState(null);

    const close = useCallback(() => setOpenIndex(null), []);
    const prev = useCallback(
        () =>
            setOpenIndex((i) =>
                i === null ? null : (i - 1 + GALLERY.length) % GALLERY.length,
            ),
        [],
    );
    const next = useCallback(
        () =>
            setOpenIndex((i) =>
                i === null ? null : (i + 1) % GALLERY.length,
            ),
        [],
    );

    useEffect(() => {
        if (openIndex === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [openIndex, close, prev, next]);

    return (
        <section
            id="galerie"
            data-testid="gallery-section"
            className="section-y relative"
        >
            <div className="container-em">
                <div className="max-w-3xl">
                    <Reveal>
                        <span className="font-accent text-xs uppercase tracking-[0.3em] text-primary">
                            Galerie
                        </span>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2
                            data-testid="gallery-title"
                            className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-tight tracking-tight text-balance"
                        >
                            Einblicke in Deine Wohnung.
                        </h2>
                    </Reveal>
                </div>

                <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
                    {GALLERY.map((img, i) => (
                        <Reveal key={img.src} delay={(i % 3) * 0.05} y={16}>
                            <button
                                type="button"
                                onClick={() => setOpenIndex(i)}
                                data-testid={`gallery-item-${i}`}
                                className="mb-5 block w-full overflow-hidden rounded-3xl group cursor-zoom-in"
                                aria-label={`Bild öffnen: ${img.alt}`}
                            >
                                <img
                                    src={img.src}
                                    alt={img.alt}
                                    loading="lazy"
                                    className="w-full h-auto object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                                />
                            </button>
                        </Reveal>
                    ))}
                </div>
            </div>

            {/* Lightbox */}
            {openIndex !== null && (
                <div
                    data-testid="gallery-lightbox"
                    className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
                    onClick={close}
                >
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            close();
                        }}
                        aria-label="Schließen"
                        data-testid="gallery-lightbox-close"
                        className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/90 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <X size={26} />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            prev();
                        }}
                        aria-label="Vorheriges Bild"
                        data-testid="gallery-lightbox-prev"
                        className="absolute left-2 sm:left-6 text-white/90 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <ChevronLeft size={30} />
                    </button>
                    <img
                        src={GALLERY[openIndex].src}
                        alt={GALLERY[openIndex].alt}
                        onClick={(e) => e.stopPropagation()}
                        className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl"
                    />
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            next();
                        }}
                        aria-label="Nächstes Bild"
                        data-testid="gallery-lightbox-next"
                        className="absolute right-2 sm:right-6 text-white/90 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <ChevronRight size={30} />
                    </button>
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm font-accent tracking-wider">
                        {openIndex + 1} / {GALLERY.length}
                    </div>
                </div>
            )}
        </section>
    );
}
